// The install-time stamp seed primitive. Deliberately knows NOTHING about the install
// flow: it turns a descriptor (what was deployed) into an InstalledStamp. The installer
// calls it through `m8t platform seed-stamp`; a CD-side seeder can generate its own
// descriptor and call exactly the same code.
import { platformTag } from "@m8t-stack/platform-release";
import type { InstalledStamp, ImageStampEntry, PersonaStampEntry } from "@m8t-stack/platform-release";

export const IMAGE_COMPONENTS = ["gateway", "codingAgent", "azureExecutor"] as const;
export type ImageComponentKey = (typeof IMAGE_COMPONENTS)[number];

export interface DescriptorImage { ref: string; tag: string; digest: string }
export interface InstallDescriptor {
  schemaVersion: 1;
  platformVersion: string; // bare X.Y.Z — a REAL channel release version
  generatedFrom: { commit: string; generatedAt: string };
  images: Record<ImageComponentKey, DescriptorImage>;
  /** Absent when the generating build could not observe its own tree. */
  content?: { personas: Record<string, string>; infra: string; brainSeedsRev: string };
}

const BARE_SEMVER = /^\d+\.\d+\.\d+$/;

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** Dependency-free structural validation. Returns [] when valid, else error strings. */
export function validateDescriptor(raw: unknown): string[] {
  const errors: string[] = [];
  if (!isObj(raw)) return ["descriptor is not an object"];
  if (raw.schemaVersion !== 1) errors.push("schemaVersion must be the integer 1");
  if (typeof raw.platformVersion !== "string" || !BARE_SEMVER.test(raw.platformVersion)) {
    // TAG form here would mean someone fed a stamp value back in; the descriptor is the
    // ONE place the bare release version lives.
    errors.push(`platformVersion must be a bare X.Y.Z release version, got ${JSON.stringify(raw.platformVersion)}`);
  }
  if (!isObj(raw.generatedFrom)) {
    errors.push("generatedFrom is required");
  } else {
    if (typeof raw.generatedFrom.commit !== "string") {
      errors.push("generatedFrom.commit is required");
    }
    if (typeof raw.generatedFrom.generatedAt !== "string") {
      errors.push("generatedFrom.generatedAt is required");
    }
  }
  const images = raw.images;
  if (!isObj(images)) {
    errors.push("images is required");
  } else {
    for (const key of IMAGE_COMPONENTS) {
      const img = images[key];
      if (!isObj(img)) { errors.push(`images.${key} is required`); continue; }
      if (typeof img.ref !== "string" || img.ref.length === 0) errors.push(`images.${key}.ref is required`);
      if (typeof img.tag !== "string" || img.tag.length === 0) errors.push(`images.${key}.tag is required`);
      if (typeof img.digest !== "string") errors.push(`images.${key}.digest must be a string (may be empty)`);
    }
  }
  const content = raw.content;
  if (content !== undefined) {
    if (!isObj(content)) {
      errors.push("content must be an object when present");
    } else {
      if (!isObj(content.personas)) errors.push("content.personas must be an object");
      if (typeof content.infra !== "string") errors.push("content.infra must be a string");
      if (typeof content.brainSeedsRev !== "string") errors.push("content.brainSeedsRev must be a string");
    }
  }
  return errors;
}

/**
 * Build the stamp a fresh install should carry. Version forms are load-bearing:
 * `platformVersion` is TAG form (platform-vX.Y.Z), component tags stay v-prefixed
 * exactly as the converge engine writes them.
 *
 * `previousPlatformVersion` is null — a seeded install has no prior release, so there
 * is no rollback target to claim. `foundryVersion` is "" because the seed does not read
 * back a deployed prompt version (converge's noop branch writes "" in the same case).
 */
export function buildSeedStamp(d: InstallDescriptor, cliVersion: string, nowIso: string): InstalledStamp {
  const img = (k: ImageComponentKey): ImageStampEntry => ({
    tag: d.images[k].tag,
    digest: d.images[k].digest,
    state: "managed",
  });
  const personas: Record<string, PersonaStampEntry> = {};
  for (const [name, treeSha] of Object.entries(d.content?.personas ?? {})) {
    personas[name] = { treeSha, foundryVersion: "" };
  }
  const stamp: InstalledStamp = {
    schemaVersion: 1,
    platformVersion: platformTag(d.platformVersion),
    previousPlatformVersion: null,
    updatedAt: nowIso,
    lastResult: "success",
    cli: cliVersion,
    origin: "install-seed",
    components: {
      gateway: img("gateway"),
      codingAgent: img("codingAgent"),
      azureExecutor: img("azureExecutor"),
      personas,
      infra: { treeSha: d.content?.infra ?? "" },
    },
  };
  if (d.content?.brainSeedsRev) stamp.components.brainSeeds = { seedRev: d.content.brainSeedsRev };
  return stamp;
}
